// MVP MatchStats tile lists for the Copilot STATS tab.
//
// The Copilot STATS grid reads its tiles from these caption definitions
// (JSONata files under gfx_PermScore/captions/), NOT from the in-app DDS
// calc. Each name maps to a `<name>.json` caption that carries the stat's
// strTitle and the $home/$away JSONata that computes its values.
//
// For the MVP we curate exactly which captions appear (and their order).
// Everything else remains reachable via the normal Stats caption shortcut.
// Singles are plain `MatchStats_*`; doubles are `MatchStats_x2_*` and render
// two stats under one graphic title.

// Order = the copilot STATS grid reading order (5 columns, row by row), per OPUS-1842:
//   Row 1: Possession, Poss last 10, Attempts, Shots on target, Touches in opp box
//   Row 2: Corners, Passes, Pass accuracy, Passes in final third, Offsides
//   Row 3: Poss 2nd half, 2nd half attempts, 2nd half shots on target, Fouls, Yellow cards
// A caption missing from the scene is left out of the grid until it is added.
export const SINGLE_STAT_CAPTIONS = [
  'MatchStats_Possession',
  'MatchStats_PossessionLast10minutes',
  'MatchStats_Attempts',
  'MatchStats_ShotsOnTarget',
  'MatchStats_TouchesInOppositionBox',
  'MatchStats_Corners',
  'MatchStats_TotalPasses',
  'MatchStats_PassAccuracy',
  'MatchStats_PassesInFinalThird',
  'MatchStats_Offsides',
  'MatchStats_2ndHalfPossession',
  'MatchStats_2ndHalfAttempts',
  'MatchStats_2ndHalfShotsOnTarget',
  'MatchStats_Fouls',
  'MatchStats_YellowCards',
];

export const DOUBLE_STAT_CAPTIONS = [
  'MatchStats_x2_Attempts_OnTarget',
  'MatchStats_x2_Possession_Attempts',
  'MatchStats_x2_Possession_TouchesInOppBox',
  'MatchStats_x2_Passes_PassAccuracy',
  'MatchStats_x2_Attempts_1st_2nd',
  'MatchStats_x2_AttemptsOnTarget_1st_2nd',
  'MatchStats_x2_Possession_1st_2nd',
  'MatchStats_x2_TouchesInOppBox_1st_2nd',
  'MatchStats_x2_2HAttempts_2HOnTarget',
  'MatchStats_x2_TouchesInOppBox_Shots',
  'MatchStats_x2_PassesInFinalThird_1st_2nd',
];

export const ALL_MATCHSTATS_CAPTIONS = [...SINGLE_STAT_CAPTIONS, ...DOUBLE_STAT_CAPTIONS];

// ADVANCED tab in grid order. `kind`: title only (team/player picked in Maestro),
// twoWay home/away, threeWay home/draw/away. `root`: its own <ROOT>_IN / <ROOT>_OUT.
export const ADVANCED_STAT_CAPTIONS = [
  { name: 'TouchMap', kind: 'title', title: 'Touch Map', root: 'TOUCH_MAP' },
  { name: 'AttackingAreas', kind: 'title', title: 'Attacking Areas', root: 'ATTACKING_THIRDS' },
  { name: 'AttackingThreat', kind: 'twoWay', title: 'Attacking Threat', root: 'ATTACKING_THREAT' },
  { name: 'PenaltyMap', kind: 'title', title: 'Penalty Map', root: 'PENALTY_MAP' },
  { name: 'GoalsOverall', kind: 'title', title: 'Goals Overall', root: 'GOLDEN_BOOT' },
  { name: 'WinProbability', kind: 'threeWay', title: 'Win Probability', root: 'WIN_PROBABILITY' },
];

// Action roots of every stats graphic on gfx_PermScore: Single/Double share MATCH_STATS.
export const STAT_ACTION_ROOTS = ['MATCH_STATS', ...ADVANCED_STAT_CAPTIONS.map((c) => c.root)];

// The stats root an action belongs to, e.g. 'TOUCH_MAP_IN' -> 'TOUCH_MAP' (side: 'IN' | 'OUT').
export const statRootOf = (action, side) =>
  STAT_ACTION_ROOTS.find((root) => String(action || '').endsWith(`${root}_${side}`)) || null;
